import { useMemo, useEffect, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Plane, useTexture } from "@react-three/drei";
import { createPlaneMaterial } from "./materials/grass_plane/plane";
import { createGrassMaterial } from "./materials/grass_plane/grass";
import { getRandomNoiseFile } from "../helpers/randomNoiseFile";
import {
    downloadPoissonPoints,
    getOrGeneratePoissonPoints,
} from "../helpers/poissonCaching";

export function GrassPlane({
    position = [0.0, 0.0, 0.0],
    size = [1.0, 1.0],
}: {
    position?: [number, number, number];
    size?: [number, number];
}) {
    const [poissonPoints, setPoissonPoints] = useState<number[][]>([]);

    useEffect(() => {
        let cancelled = false;
        getOrGeneratePoissonPoints(size, 0.1, 0.2, 10).then((points) => {
            if (!cancelled) setPoissonPoints(points);
            // downloadPoissonPoints(points, {size: size, minDistance: 0.1, maxDistance: 0.2, tries: 10})
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // textures
    const flowerTexture = useTexture("/textures/flower.png");
    const grassTexture = useTexture("/textures/grass.png");
    const noiseFile = useMemo(() => getRandomNoiseFile(), []);
    const noiseTexture = useTexture(noiseFile);

    useEffect(() => {
        grassTexture.colorSpace = THREE.SRGBColorSpace;
        grassTexture.minFilter = THREE.NearestFilter;
        grassTexture.magFilter = THREE.NearestFilter;
        grassTexture.generateMipmaps = false;
        grassTexture.needsUpdate = true;
    }, [grassTexture]);

    useEffect(() => {
        flowerTexture.colorSpace = THREE.SRGBColorSpace;
        flowerTexture.minFilter = THREE.NearestFilter;
        flowerTexture.magFilter = THREE.NearestFilter;
        flowerTexture.generateMipmaps = false;
        flowerTexture.needsUpdate = true;
    }, [flowerTexture]);

    useEffect(() => {
        noiseTexture.wrapS = THREE.RepeatWrapping;
        noiseTexture.wrapT = THREE.RepeatWrapping;
    }, [noiseTexture]);

    // poisson => local
    const positions = useMemo(() => {
        return poissonPoints.map((point) => ({
            x: point[0] - size[0] / 2.0,
            y: 0,
            z: point[1] - size[1] / 2.0,
        }));
    }, [poissonPoints]);

    // materials
    const planeMaterial = useMemo(
        () => createPlaneMaterial(noiseTexture, size, position),
        [noiseTexture, size, position],
    );
    const grassMaterial = useMemo(() => {
        const mat = createGrassMaterial(
            grassTexture,
            flowerTexture,
            noiseTexture,
            size,
            position,
        );
        mat.needsUpdate = true;
        return mat;
    }, [grassTexture, noiseTexture, size, position]);

    // Pmesh
    const instancedMesh = useMemo(() => {
        if (positions.length === 0) return null;

        const geometry = new THREE.PlaneGeometry(0.2, 0.2);
        const mesh = new THREE.InstancedMesh(
            geometry,
            grassMaterial,
            positions.length,
        );

        const dummy = new THREE.Object3D();
        positions.forEach((pos, i) => {
            dummy.position.set(pos.x, pos.y, pos.z);
            dummy.updateMatrix();
            mesh.setMatrixAt(i, dummy.matrix);
        });
        mesh.instanceMatrix.needsUpdate = true;
        mesh.receiveShadow = true;

        return mesh;
    }, [positions, grassMaterial]);

    // set grass uniforms for wind
    useFrame((state) => {
        if (grassMaterial.userData.uniforms) {
            grassMaterial.userData.uniforms.time.value =
                state.clock.elapsedTime;
        }
    });

    // scene it up
    return (
        <group position={position}>
            <Plane
                args={size}
                rotation={[-Math.PI / 2.0, 0.0, (4 * Math.PI) / 2.0]}
                material={planeMaterial}
                receiveShadow
            />
            {instancedMesh && <primitive object={instancedMesh} />}
        </group>
    );
}
